import { useEffect, useState, type PropsWithChildren, type ReactNode } from "react";
import type { TabId } from "../types/tab-id.ts";
import { Tabs, TabsContent } from "../../../components/base/tabs.tsx";
import { cn } from "../../../utils/cn.ts";
import { TrackEvent } from "../../../utils/analytics.ts";

export interface TabDefinition {
  id: TabId;
  label: string;
  icon: string;
  // When false, the tab is excluded from rendering entirely (e.g. when content is unavailable)
  includeTab: boolean;
  content: ReactNode;
  // When true, activating the tab doesn't POST a reporting event - e.g. an external video, whose
  // click is reported server-side by the /downloads redirect when the user actually opens it
  skipClickReporting?: boolean;
}

export interface CachingTabsViewProps {
  tabs: TabDefinition[];
  // The rendered tab buttons/list — passed as a slot so the caller controls layout and styling
  tabsList: ReactNode;
  activeTabId: TabId;
  onTabChange: (tabId: TabId) => void;
  className?: string;
  contentClassName?: string;
  tabsContentClassName?: string;
  // Controls whether the tab list renders above or below the content area
  tabsListPosition?: "top" | "bottom";
  // Property identity used when POSTing tab-click reporting events.
  // Reporting is keyed on the back-office reference; source is optional (omitted means espc).
  reportingPropertyReference?: number;
  reportingPropertySource?: string;
}

const TAB_REPORTING_ENDPOINTS: Partial<Record<TabId, string>> = {
  "virtual-tour": "/reporting/virtual-tour-clicked",
  video: "/reporting/video-clicked",
  "floor-plan": "/reporting/floor-plan-viewed",
};

type GalleryNavType = "photos" | "virtual_tour" | "external_video" | "map" | "street_view";
const TAB_GALLERY_NAV_TYPE: Partial<Record<TabId, GalleryNavType>> = {
  photos: "photos",
  "virtual-tour": "virtual_tour",
  video: "external_video",
  map: "map",
  "street-view": "street_view",
};

/**
 * A tabs container that keeps previously visited tab content mounted in the DOM.
 *
 * By default, switching tabs would unmount the previous tab's content, destroying
 * any internal state (scroll position, loaded data, etc.). This component avoids
 * that by tracking which tabs have been visited and keeping their content mounted,
 * hiding inactive tabs via CSS rather than unmounting them.
 *
 * Tabs that haven't been visited yet are not rendered at all, avoiding unnecessary
 * work for content the user may never view.
 */
export const CachingTabsView = ({
  tabs,
  tabsList,
  activeTabId,
  onTabChange,
  className,
  contentClassName,
  tabsContentClassName,
  tabsListPosition = "bottom",
  reportingPropertyReference,
  reportingPropertySource,
  children,
}: PropsWithChildren<CachingTabsViewProps>) => {
  // Track which tabs have been activated at least once so their content stays mounted
  const [visitedTabs, setVisitedTabs] = useState<Set<TabId>>(new Set([activeTabId]));

  useEffect(() => {
    // Add the newly active tab to the visited set if it isn't already there
    setVisitedTabs((prev) => (prev.has(activeTabId) ? prev : new Set(prev).add(activeTabId)));
  }, [activeTabId]);

  const handleTabChange = (tabId: TabId) => {
    const endpoint = TAB_REPORTING_ENDPOINTS[tabId];
    const skipClickReporting = tabs.find((x) => x.id === tabId)?.skipClickReporting;
    if (endpoint && !skipClickReporting && reportingPropertyReference != null) {
      fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reference: reportingPropertyReference, source: reportingPropertySource }),
      }).catch(() => {
        // reporting is best-effort; suppress errors
      });
    }

    if (tabId === "floor-plan") {
      TrackEvent({ name: "floorplan_clicked", value: 15 });
    }

    const galleryNavType = TAB_GALLERY_NAV_TYPE[tabId];
    if (galleryNavType) {
      TrackEvent({ name: "pdetails_gallery_navigation_click", type: galleryNavType });
    }

    onTabChange(tabId);
  };

  return (
    <Tabs
      value={activeTabId}
      activationMode={"manual"}
      onValueChange={(value) => handleTabChange(value as TabId)}
      className={className}
    >
      {tabsListPosition === "top" && tabsList}
      <div className={contentClassName}>
        {tabs
          // Only render tabs that are included and have been visited at least once
          .filter((x) => x.includeTab && visitedTabs.has(x.id))
          .map((tab) => (
            <TabsContent
              key={tab.id}
              value={tab.id}
              tabIndex={-1}
              // forceMount keeps the content in the DOM even when the tab is inactive
              forceMount
              // Hide inactive tabs via CSS instead of unmounting them
              className={cn("data-[state=inactive]:hidden  flex  flex-col flex-1 min-h-0", tabsContentClassName)}
            >
              {tab.content}
            </TabsContent>
          ))}
        {children}
      </div>
      {tabsListPosition === "bottom" && tabsList}
    </Tabs>
  );
};
